<template>
	<template>
		<!-- Meta tags -->

		<Head>
			<title>
				{{ ratingState?.ratingData?.company?.name ? ratingState.ratingData.company.name : $t("szmd") }} -
				DreamJobs
			</title>
			<link rel="canonical" :href="'https://' + globalConfig.host + $route.fullPath" />
			<link rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		</Head>
	</template>
	<section class="flex flex-col bg-[#F9FBFE]">
		<!-- Login Section -->
		<template v-if="ratingState.state == 'no_authentication'">
			<div class="djcontainer mt-5 mx-4 max-sm:max-w-full md:mx-auto">
				<div class="md:col-span-1"></div>
				<SzmdBox :header="$t('noauth.title')" class="md:col-span-2">
					<p class="mb-8">
						{{ $t("noauth.description") }}
					</p>
					<div class="mt-8 justify-items-center">
						<BaseButton
							@click="$router.push({ path: `/${$i18n.locale}/login`, query: { r: $route.fullPath } })"
							type="tertiary"
							class="capitalize w-full max-w-[335px] mx-auto"
							tyle="
									color: #000;
									background-color: #fff;
								"
						>
							{{ $t("loginBtn") }}
						</BaseButton>
					</div>
				</SzmdBox>
			</div>
		</template>

		<!-- Loading Section -->
		<template v-else-if="ratingState.state == 'loading'">
			<div class="z-40 w-full bg-white py-4 text-white bg-dj-red">
				<div class="text-sm text-center text-white mb-4 mt-2">
					<span class="dj-loader mr-extrasmall"></span>
				</div>
				<div class="text-2xl text-center my-2 font-bold">
					{{ $t("loading") }}
				</div>
			</div>
		</template>

		<!-- Voting not available alert section -->
		<template v-else-if="isAuth && ratingState.state == 'voting_not_available'">
			<div class="djcontainer mt-5 mx-4 max-sm:max-w-full md:mx-auto">
				<div class="md:col-span-1"></div>
				<SzmdBox :header="$t('szmdTexts.votingNotAvailable.title')" :show-logo="true" class="md:col-span-2">
					<p class="mb-8">
						{{ $t("szmdTexts.votingNotAvailable.description") }}
					</p>
				</SzmdBox>
			</div>
		</template>

		<!-- Company rating section -->
		<template
			v-else-if="isAuth && ratingState.state == 'voting'"
			:key="`base-rating-form-${ratingState.ratingData.company.id || 'loading'}`"
		>
			<div class="w-full bg-white p-4 pb-0 text-white bg-dj-red">
				<h1 class="text-3xl font-bold text-white mb-6 mt-2 text-center">
					{{ $t("company.box.freeSzmdBox2025") }}
				</h1>
				<h2 class="text-center text-xl text-white">
					{{ szmdPrizes ? $t("szmdTexts.szmdBoxVote.voteForFavorite") : $t("szmdNoPrizes.voteForFavorite") }}
				</h2>
				<hr class="mt-8 bg-white mx-auto" style="width: 100px; height: 2px" />
			</div>
			<div class="sticky top-0 z-40 w-full bg-white p-4 text-white bg-dj-red">
				<div class="font-bold text-white mt-2 text-center">
					{{
						$t("szmdTexts.szmdBoxVote.remainingVotes", {
							count: ratingState.ratingData.user.remaining_votes,
						})
					}}
				</div>
				<h1 class="text-3xl font-bold text-white mb-6 text-center">
					{{ ratingState.ratingData.company.name }}
				</h1>
				<div class="my-2 flex gap-5 justify-center">
					<BaseButton
						type="secondary"
						style="color: #000; background-color: #fff; min-height: 30px"
						@click="handleCompanyRatingSelected(1)"
						class="group px-8"
					>
						<BaseIcon
							class="mr-[10px] text-xl text-dj-red group-hover:hidden"
							icon="ic:outline-favorite-border"
							color="#E1415A"
						/>
						<BaseIcon
							class="mr-[10px] text-xl text-dj-red hidden group-hover:block"
							icon="ic:outline-favorite"
							color="#E1415A"
						/>
						{{ $t("szmdTexts.szmdBoxVote.like") }}
					</BaseButton>

					<BaseButton
						type="secondary"
						style="color: #000; background-color: #fff; min-height: 30px"
						@click="getSzmdRatingData()"
						class="px-8"
					>
						{{ $t("szmdTexts.szmdBoxVote.unlike") }}

						<div
							class="ml-[10px] grid place-items-center w-[20px] h-[20px] cursor-pointer rounded-full bg-blue-light border border-dj-red"
						>
							<BaseIcon class="text-[16px]" icon="ic:outline-navigate-next" />
						</div>
					</BaseButton>
				</div>
			</div>
			<div class="container min-h-[640px] mt-16">
				<div
					class="masonrycontainer mb-10"
					:class="{
						'columns-1 max-w-[30%] max-sm:max-w-full':
							ratingState.getImageCountByFreeType('szmd-box-2025') === 1,
						'columns-2 max-w-[60%] max-sm:max-w-full':
							ratingState.getImageCountByFreeType('szmd-box-2025') === 2,
					}"
				>
					<template
						temp-for="(box, index) in ratingState.ratingData.company.boxes"
						:key="index"
						v-for="(box, index) in ratingState.getBoxesByFreeType('szmd-box-2025')"
					>
						<template v-for="(image, index) in ratingState.getFreeBoxImages(index)" :key="index">
							<div class="break-inside-avoid-column relative">
								<DjBox
									class="bg-white cursor-pointer text-black max-w-[440px] overflow-hidden relative"
								>
									<div
										class="flex justify-between items-start mt-5 mr-5 ml-10"
										style="position: absolute; top: -20px"
									></div>
									<BaseCloudImage
										class="max-w-full"
										:public-id="image.cloudinary"
										:transformation="[{ width: 440, crop: 'scale' }]"
										imageClass="w-full"
									/>

									<h3 class="m-3 md:m-6 text-lg md:text-xl font-medium">
										{{ $t("company.box.freeSzmdBox2025") }}
									</h3>

									<div
										class="content-rte m-3 md:m-6"
										v-html="transformText(image.description?.[$i18n.locale] || '')"
									></div>
								</DjBox>
							</div>
						</template>
					</template>
				</div>
			</div>
		</template>

		<!-- No more ratings section -->
		<template v-else-if="isAuth && ratingState.state == 'cannot_rate_this_company'">
			<div class="djcontainer mt-5 mx-4 max-sm:max-w-full md:mx-auto">
				<div class="md:col-span-1"></div>
				<SzmdBox :header="$t('szmdTexts.cannotRateThisCompany.title')" :showLogo="true" class="md:col-span-2">
					<p class="mb-8">
						{{ $t("szmdTexts.cannotRateThisCompany.description") }}
					</p>
					<BaseButton
						type="secondary"
						style="color: #000; background-color: #fff; min-height: 30px"
						@click="getSzmdRatingData()"
						class="px-8"
					>
						{{ $t("szmdTexts.szmdBoxVote.unlike") }}

						<div
							class="ml-[10px] grid place-items-center w-[20px] h-[20px] cursor-pointer rounded-full bg-blue-light border border-dj-red"
						>
							<BaseIcon class="text-[16px]" icon="ic:outline-navigate-next" />
						</div>
					</BaseButton>
				</SzmdBox>
			</div>
		</template>

		<!-- No more ratings section -->
		<template v-else-if="isAuth && ratingState.state == 'already_rated_to_company_this_week'">
			<div class="djcontainer mt-5 mx-4 max-sm:max-w-full md:mx-auto">
				<div class="md:col-span-1"></div>
				<SzmdBox
					:header="$t('szmdTexts.alreadyRatedToCompanyThisWeek.title')"
					:showLogo="true"
					class="md:col-span-2"
				>
					<p class="mb-8">
						{{ $t("szmdTexts.alreadyRatedToCompanyThisWeek.description") }}
					</p>
				</SzmdBox>
			</div>
		</template>

		<!-- Error Section -->
		<template v-else-if="isAuth && ratingState.state == 'error'">
			<div class="djcontainer mt-5 mx-4 max-sm:max-w-full md:mx-auto">
				<div class="md:col-span-1"></div>
				<SzmdBox :show-logo="true" :header="$t('errors.somethingWentWrong')" class="md:col-span-2"></SzmdBox>
			</div>
		</template>
	</section>
</template>

<script lang="ts" setup>
import { onMounted, watch, computed, WatchStopHandle } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { SzmdRatingService, SzmdRatingState } from "~/services/SzmdRatingService";
import { textTransformService, TransformationType, TransformationConfig } from "~/services/TextTransformService";

import globalConfig from "~/composables/globalConfig";

definePageMeta({
	pageType: "szmd-box-voting",
});

const { szmdPrizes } = useSzmdConfig();

const route = useRoute();
const router = useRouter();

useI18n();

const isAuth = useState("isAuth");

const slug = computed(() => route.query.slug as string);

// Initialize rating state and service
const ratingState = new SzmdRatingState();
const ratingService = new SzmdRatingService(ratingState);

onMounted(async () => {
	// We'll set a timeout that waits 5000 milliseconds (5 seconds)
	setTimeout(() => {
		// After the 5-second delay, we check the value of isAuth
		if (!isAuth.value) {
			// If isAuth is false, we set the loadStatus
			ratingState.setState("no_authentication");
		}
	}, 5000);

	// Wach for authentication state and fetch SZMD rating data when authenticated
	let stopAuthWatcher: WatchStopHandle;

	stopAuthWatcher = watch(
		isAuth,
		async (newValue) => {
			if (newValue) {
				if (stopAuthWatcher) stopAuthWatcher(); // Csak ha már létezik
				await getSzmdRatingData();
			}
		},
		{ immediate: true }
	);
});

/**
 * Fetch SZMD rating data for the authenticated user.
 */
async function getSzmdRatingData() {
	await ratingService.fetchRatingData(slug.value);
	slug.value = null;
	router.push(route.path);
}

/**
 * Handle company rating selection.
 *
 * When the user rates a company, save the rating and proceed to the company like screen
 */
function handleCompanyRatingSelected(ratingValue: number) {
	console.error("Company Rating selected:", ratingValue);

	ratingService.submitRatings(ratingValue);
}

/**
 * Transform text with configurable transformations
 */
function transformText(text: string): string {
	const transformConfigs: TransformationConfig[] = [
		{
			type: TransformationType.LINE_BREAKS,
		},
		{
			type: TransformationType.YOUTUBE_LINKS,
		},
		{
			type: TransformationType.URL_TO_LINKS,
			options: {
				className: "text-dj-blue",
			},
		},
	];

	return textTransformService.transform(text, transformConfigs);
}
</script>
<style>
body {
	overflow-x: initial;
}

.dj-loader {
	width: 40px;
	height: 40px;
	border: 6px solid #fff;
	border-bottom-color: transparent;
	vertical-align: middle;
	border-radius: 50%;
	display: inline-block;
	box-sizing: border-box;
	animation: rotation 1s linear infinite;
}

@keyframes rotation {
	0% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(360deg);
	}
}
</style>
